// src/components/map/CompareCreditsFooter.jsx
//
// Explore's compare footer (REM-707, S-e slice 4b; DB 2026-11-03): one footer
// along the whole bottom edge that never moves. The slider moves only the
// imagery. Before, each side's footer was sized and placed by the slider, so
// the logos and credits slid across the map as it was dragged.
//
//   ┌──────────────────────────────────┬───────────────────────────────────┐
//   │ [mapbox logo] left map's credits │ right map's credits [mapbox logo] │
//   └──────────────────────────────────┴───────────────────────────────────┘
//
// Each half is its own map's: the left map's logo and credits from the left
// end, the right map's credits (its own Copernicus year) and logo to the right
// end. At 0% or 100% one side's credits sit over the other map's pixels; both
// stay, as each map is still on the page and its credit is owed (Mapbox's
// terms ask for the logo and the attribution on every map shown).
//
// Narrow: a half under COMPACT_BELOW_PX (480 px) wraps to two lines, the logo
// on the first at its end, the credits under it. The parts (the logo, the
// credits list, the 24 px veil) are MapCreditsFooter's.
import { useLayoutEffect, useRef, useState } from "react";
import { COMPACT_BELOW_PX } from "../../lib/map/attribution";
import { useMapCredits } from "./useMapCredits";
import { CreditsText, MapboxLogo } from "./MapCreditsFooter";
import "./mapCreditsFooter.css";

function Side({ map, side, testid }) {
  // The map back from the hook only while it is live: nothing here calls into a removed map.
  const { credits, map: live } = useMapCredits(map);
  const text = <CreditsText shown={credits} map={live} />;
  return (
    <div className="aip-credits-side" data-side={side} data-testid={testid}>
      {side === "start" ? <><MapboxLogo />{text}</> : <>{text}<MapboxLogo /></>}
    </div>
  );
}

/**
 * @param {object} p
 * @param {object|null} p.mapA            the left map (Explore's main map)
 * @param {object|null} p.mapB            the right map (the compare map)
 * @param {(px: number) => void} [p.onHeight]  the footer's height, for the bottom stack to sit above
 */
export default function CompareCreditsFooter({ mapA, mapB, onHeight = null }) {
  const ref = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const heightCb = useRef(onHeight);
  useLayoutEffect(() => { heightCb.current = onHeight; });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setNarrow(w > 0 && w / 2 < COMPACT_BELOW_PX);
      heightCb.current?.(Math.ceil(el.getBoundingClientRect().height));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="aip-credits-footer aip-credits-footer--pair" data-chip-avoid data-testid="explore-credits" data-narrow={narrow ? "" : undefined}>
      <Side map={mapA} side="start" testid="explore-credits-a" />
      <Side map={mapB} side="end" testid="explore-credits-b" />
    </div>
  );
}
